import React, { useState, useRef } from 'react'
import styled from '@emotion/styled'
import { find } from 'lodash'

import { Box } from '../Box/Box'
import { Stack } from '../Stack/Stack'
import { Inline } from '../Inline/Inline'
import { Icon } from '../Icon/Icon'
import { Text } from '../Text/Text'
import { FieldLabel, FieldLabelProps } from '../FieldLabel/FieldLabel'
import { FieldMessage } from '../FieldMessage/FieldMessage'
import useOnClickOutside from '../../hooks/useOnClickOutside'
import iconMap from '../../assets/icons/icon-map'

interface Option {
  text?: string
  value: any
  icon?: {
    name: keyof typeof iconMap
    size?: 'xs' | 's' | 'm' | 'l' | 'xl'
    tone?: 'light' | 'neutral' | 'action' | 'positive' | 'negative' | 'caution'
    rotate?: number
  }
}

export interface DropdownProps extends Partial<FieldLabelProps> {
  options: Option[]
  value: any
  onChange: (value: any) => void
  placeholder?: string
  inlineBlock?: boolean
  hideSelectedText?: boolean
  attachRight?: boolean
  attachLeft?: boolean
  message?: string
  tone?: 'neutral' | 'positive' | 'negative'
  big?: boolean
  optionsMaxHeight?: string
  disabled?: boolean
  background?: string
  native?: boolean
}

export const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  hideSelectedText = false,
  label,
  labelWeight,
  secondary,
  tertiary,
  tertiaryIcon,
  tertiaryAction,
  description,
  message,
  tone,
  big,
  optionsMaxHeight = '190px',
  disabled = false,
  native = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const toggleOpen = () => {
    setIsOpen(!isOpen)
  }

  const dropdownRef = useRef(null)

  useOnClickOutside(dropdownRef, () => setIsOpen(false))

  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) setIsOpen(true)
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (highlightedIndex !== null && highlightedIndex < options.length - 1) {
          setHighlightedIndex(highlightedIndex + 1)
        } else {
          setHighlightedIndex(0)
        }
        break
      case 'ArrowUp':
        event.preventDefault()
        if (highlightedIndex !== null && highlightedIndex > 0) {
          setHighlightedIndex(highlightedIndex - 1)
        } else {
          setHighlightedIndex(options.length - 1)
        }
        break
      case 'Enter':
        event.preventDefault()
        if (highlightedIndex !== null) {
          onChange(options[highlightedIndex].value)
          setIsOpen(false)
        }
        break
      case 'Escape':
        event.preventDefault()
        onChange(null)
        setIsOpen(false)
        setHighlightedIndex(null)
    }
  }

  const { text, icon } = find(options, { value }) || {}

  const background = disabled
    ? 'var(--textfield-bg--disabled)'
    : props.background
    ? props.background
    : 'transparent'

  return (
    <Stack space="xs">
      {label && (
        <FieldLabel
          label={label}
          labelWeight={labelWeight}
          secondary={secondary}
          tertiary={tertiary}
          tertiaryIcon={tertiaryIcon}
          tertiaryAction={tertiaryAction}
          description={description}
        />
      )}
      {!native ? (
        <Wrapper
          ref={dropdownRef}
          tabIndex={0}
          onClick={toggleOpen}
          onKeyDown={onKeyDown}
          big={big}
          background={background}
          {...props}
        >
          <SelectionBox
            height={big ? undefined : 'calc(var(--space--xl) - 2px);'}
            paddingX={big ? 'm' : 's'}
            paddingY={big ? 'm' : undefined}
            background={background}
          >
            <Inline space="s" alignY="center">
              {icon && icon.name && (
                <Icon
                  name={icon.name}
                  size={icon.size || 'm'}
                  tone={icon.tone}
                  rotate={icon.rotate}
                />
              )}
              {!hideSelectedText && text && (
                <UnselectableText tone="neutral" size={big ? 'big' : undefined}>
                  {text}
                </UnselectableText>
              )}
              {!value && (
                <UnselectableText size={big ? 'big' : undefined} tone="light">
                  {placeholder}
                </UnselectableText>
              )}
            </Inline>
            <Box paddingLeft="xs">
              <Icon name="arrow" tone="light" size="s" />
            </Box>
          </SelectionBox>

          {isOpen && (
            <OptionsStack className="options-stack" big={big} optionsMaxHeight={optionsMaxHeight}>
              {options.map((option, i) => (
                <OptionDiv
                  key={i}
                  highlighted={highlightedIndex === i}
                  divider={i > 0}
                  onClick={() => onChange(option.value)}
                  onMouseEnter={() => setHighlightedIndex(i)}
                >
                  {option.icon && (
                    <Box paddingRight="s">
                      <Icon
                        name={option.icon.name}
                        size={option.icon.size || 'm'}
                        tone={option.icon.tone}
                        rotate={option.icon.rotate}
                      />
                    </Box>
                  )}
                  <Text size={big ? 'big' : undefined}>{option.text}</Text>
                </OptionDiv>
              ))}
            </OptionsStack>
          )}
        </Wrapper>
      ) : (
        <NativeSelect
          big={big}
          onChange={(e) => onChange(e.target.value)}
          value={value}
          disabled={disabled}
          defaultValue={'default'}
        >
          <option value="default" disabled>
            {placeholder}
          </option>
          {options.map((option, i) => (
            <option key={i} value={option.value}>
              {option.text}
            </option>
          ))}
        </NativeSelect>
      )}

      {message && <FieldMessage tone={tone === 'neutral' ? 'light' : tone}>{message}</FieldMessage>}
    </Stack>
  )
}

const Wrapper = styled.div<Partial<DropdownProps>>`
  position: relative;
  cursor: pointer;
  border: 1px solid var(--colors-subtle);
  border-radius: var(--border-radius--xxs);
  outline: none;

  ${(p) =>
    p.big ||
    `
    height: var(--space--xl);
  `}

  ${(p) =>
    p.inlineBlock &&
    `
    display: inline-block;
  `}

  ${(p) =>
    p.attachLeft &&
    `
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: none;

    .options-stack {
      border-top-left-radius: 0;
    }
  `}

  ${(p) =>
    p.attachRight &&
    `
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;

    .options-stack {
      border-top-right-radius: 0;
    }
  `}
`

const SelectionBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: var(--border-radius--xxs);
`

const UnselectableText = styled(Text)`
  user-select: none;
`

interface OptionsStackProps {
  big?: boolean
  optionsMaxHeight?: string
}

const OptionsStack = styled(Stack)<OptionsStackProps>`
  position: absolute;
  top: ${(p) => (p.big ? '45px' : 'calc(var(--space--xl) - 2px)')};
  left: -1px;
  width: auto;
  min-width: calc(100% + 2px);
  border: 1px solid var(--colors-subtle);
  border-radius: var(--border-radius--xxs);
  background-color: var(--colors-foreground);
  max-height: ${(p) => p.optionsMaxHeight};
  overflow-y: scroll;
  pointer-events: all;
  z-index: var(--zindex-dropdown);
`

interface OptionDivProps {
  highlighted: boolean
  divider: boolean
}

const OptionDiv = styled.div<OptionDivProps>`
  display: flex;
  align-items: center;
  height: var(--space--xl);
  padding: 0 var(--space--s);

  ${(p) =>
    p.highlighted &&
    `
    background: var(--colors-highlight);
  `}

  ${(p) =>
    p.divider &&
    `
    border-top: 1px solid var(--colors-hairline);
  `}
`
const NativeSelect = styled.select<OptionsStackProps>`
  width: 100%;
  border: 1px solid var(--colors-subtle);
  border-radius: var(--border-radius--xxs);
  color: var(--colors-light);
  padding: 6px 15px 6px 12px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTQiIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxNCAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0yLjE2MjcyIDIuMTYyNzJDMi4zNzk2OCAxLjk0NTc2IDIuNzMxNDMgMS45NDU3NiAyLjk0ODM5IDIuMTYyNzJMNyA2LjIxNDMzTDExLjA1MTYgMi4xNjI3MkMxMS4yNjg2IDEuOTQ1NzYgMTEuNjIwMyAxLjk0NTc2IDExLjgzNzMgMi4xNjI3MkMxMi4wNTQyIDIuMzc5NjggMTIuMDU0MiAyLjczMTQzIDExLjgzNzMgMi45NDgzOUw3LjM5Mjg0IDcuMzkyODRDNy4xNzU4OCA3LjYwOTc5IDYuODI0MTIgNy42MDk3OSA2LjYwNzE2IDcuMzkyODRMMi4xNjI3MiAyLjk0ODM5QzEuOTQ1NzYgMi43MzE0MyAxLjk0NTc2IDIuMzc5NjggMi4xNjI3MiAyLjE2MjcyWiIgZmlsbD0iIzg2ODg4QiIvPgo8L3N2Zz4K)
    no-repeat 100% 50%;
  background-size: 13px 13px;
  background-position: bottom 50% right 12px;
  ${(p) =>
    p.big &&
    `
    height: 50px;
    font-size: 1.6rem;
    padding: 10px 16px;

  `}
`
